import { z } from 'zod';

// Password validation - enforce strong passwords
const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')
  .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character');

// Authentication schemas
export const signInSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Email is required')
    .email('Please enter a valid email address')
    .max(255, 'Email must be less than 255 characters'),
  password: z
    .string()
    .min(1, 'Password is required')
});

export const signUpSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Email is required')
    .email('Please enter a valid email address')
    .max(255, 'Email must be less than 255 characters'),
  password: passwordSchema,
  displayName: z
    .string()
    .trim()
    .min(1, 'Display name is required')
    .max(20, 'Display name must be less than 20 characters')
    .regex(/^[a-zA-Z0-9\s\-_.]+$/, 'Display name can only contain letters, numbers, spaces, hyphens, underscores, and periods')
});

// URL validation helper
const urlSchema = z
  .union([
    z.string().trim().refine((url) => {
      if (!url) return true; // Allow empty strings
      try {
        new URL(url);
        return true;
      } catch {
        return false;
      }
    }, 'Please enter a valid URL (include http:// or https://)'),
    z.null()
  ])
  .optional();

// Blog post schema
export const blogPostSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, 'Title is required')
    .max(200, 'Title must be less than 200 characters'),
  content: z
    .string()
    .trim()
    .max(50000, 'Content must be less than 50,000 characters')
    .optional()
    .default(''),
  preview: z
    .string()
    .trim()
    .max(500, 'Preview must be less than 500 characters')
    .optional(),
  facebook_url: urlSchema.nullable().optional(),
  twitter_url: urlSchema.nullable().optional(),
  instagram_url: urlSchema.nullable().optional(),
  linkedin_url: urlSchema.nullable().optional(),
  youtube_url: urlSchema.nullable().optional(),
  tiktok_url: urlSchema.nullable().optional(),
  threads_url: urlSchema.nullable().optional(),
  google_business_url: urlSchema.nullable().optional(),
  pinterest_url: urlSchema.nullable().optional(),
  snapchat_url: urlSchema.nullable().optional(),
  bluesky_url: urlSchema.nullable().optional(),
  reddit_url: urlSchema.nullable().optional(),
  substack_url: urlSchema.nullable().optional(),
  discord_url: urlSchema.nullable().optional(),
});

// Comment schema - matches server-side validation
export const commentSchema = z.object({
  content: z
    .string()
    .trim()
    .min(1, 'Comment cannot be empty')
    .max(5000, 'Comment must be less than 5000 characters'),
  author_name: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(100, 'Name must be less than 100 characters')
    .regex(/^[a-zA-Z0-9\s\-_.]+$/, 'Name can only contain letters, numbers, spaces, hyphens, underscores, and periods'),
  author_email: z
    .string()
    .trim()
    .email('Please enter a valid email address')
    .max(255, 'Email must be less than 255 characters')
});

// Helper function to sanitize text content
export const sanitizeText = (text: string): string => {
  return text
    .trim()
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .substring(0, 10000); // Limit length as additional safety
};

// Helper function to validate and sanitize URLs
export const sanitizeUrl = (url: string | undefined | null): string | null => {
  if (!url) return null;
  
  const trimmed = url.trim();
  if (!trimmed) return null;
  
  // Add protocol if missing
  if (!/^https?:\/\//.test(trimmed)) {
    return `https://${trimmed}`;
  }
  
  return trimmed;
};

export type SignInData = z.infer<typeof signInSchema>;
export type SignUpData = z.infer<typeof signUpSchema>;
export type BlogPostData = z.infer<typeof blogPostSchema>;
export type CommentData = z.infer<typeof commentSchema>;